import React, { useEffect } from 'react';

import { SseMessageChannel } from '../connection';

/** Tiny utility for working with the SSE message channel on the pos (port1) side */
export const SSE = {
  /** Post a message from the POS to all remotes */
  send: SseMessageChannel.port1.postMessage.bind(SseMessageChannel.port1),
  /**
   * Listen for messages sent to the POS
   * Returns a cleanup function, making it ideal for React.useEffect
   */
  onMessage: handler => {
    SseMessageChannel.port1.addEventListener('message', handler);
    return () =>
      SseMessageChannel.port1.removeEventListener('message', handler);
  },
};

/**
 * A suggestion is a message to the POS which requests some action be taken
 * but requires the cashier to confirm (or reject) the action
 */
type Suggestion = {
  accept: () => void;
  acceptText: string;
  reject?: () => void;
  rejectText?: string;
};

/**
 * Utility hook for defining a 'suggestion' message type and the behaviour when
 * it is accepted/rejected
 *
 * @example
 * // Handles a message like { type: 'closeWindow', payload: {delay: 5}}
 * const sugg = useSuggestion('closeWindow', ({delay=0}) => ({
 *   acceptText: `Close window after ${delay} seconds`,
 *   accept: () => setTimeout(window.close, 1e3 * delay),
 * }))
 *
 * if (!sugg) return null
 * return <>
 *   <Button onClick={sugg.accept}>{sugg.acceptText}</Button>
 *   <Button onClick={sugg.reject}>{sugg.rejectText}</Button>
 * </>
 */
export function useSuggestion<T = any>(
  /** Message type (.type property) that should create a {@link Suggestion} */
  msgType: string,
  /** Parser to convert a matching message into a {@link Suggestion} */
  toSuggestion: (msg: T, e: MessageEvent) => Suggestion,
) {
  const [suggestion, setSuggestion] = React.useState<Suggestion | null>(null);

  // Listen for incoming events
  useEffect(() => {
    return SSE.onMessage((msg: MessageEvent) => {
      if (msg.data.type === msgType) {
        setSuggestion(toSuggestion(msg.data.payload, msg));
      }
    });
  }, [msgType, toSuggestion]);

  if (!suggestion) return undefined;
  return {
    /** Text to display on the accept button */
    acceptText: suggestion.acceptText,
    /** Text to display on the reject button */
    rejectText: suggestion.rejectText ?? 'ignore',
    /** Callback for the accept button */
    accept: () => {
      suggestion.accept();
      setSuggestion(null);
    },
    /** Callback for the reject button */
    reject: () => {
      suggestion.reject?.();
      setSuggestion(null);
    },
  };
}
